import React, { useEffect, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import dayjs from "dayjs";
import RIcon from "../reservas/components/ReservasIcons";
import { BEAUTY_FALLBACK_IMAGE, HoldCountdown, resolveUrl } from "../reservas/components/ReservaShared";
import { DepositBadge } from "./components/SalonSections";
import { OpcionesSheet } from "./components/OpcionesDeServicio";
import {
  AppointmentDetails,
  ConsentCheck,
  ContactFields,
  DayPicker,
  DepositNote,
  HoldAmounts,
  HoldDetailRows,
  LinkedProfessionalCard,
  PaymentModal,
  ProfessionalNote,
  ProfessionalOptions,
  SlotPicker,
} from "./components/BookingParts";
import {
  formatDuration,
  formatLongDate,
  formatMoney,
  formatPillDate,
  lineasDeCarrito,
  opcionesPill,
  salonAddressLine,
  salonPolicyLines,
  serviceMeta,
} from "./beautyBooking";
import { claveDeLinea, nombreDeLinea, resumenDeLineas, tieneOpciones } from "../../helpers/beautyLineas";
import "../reservas/reservas.css";
import "../reservas/reservas-mobile.css";
import "./beauty.css";

const TOTAL_STEPS = 6;
const STEP_TITLES = {
  1: "¿Qué te querés hacer?",
  2: "¿Con quién te atendés?",
  3: "¿Qué día?",
  4: "¿A qué hora?",
  5: "Revisá tu turno",
  6: "Tus datos",
};
const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
  </svg>
);

const servicesLabel = (count) => `${count} ${count === 1 ? "servicio" : "servicios"}`;

const StepHeader = ({ step, salonName, onBack, linked }) => {
  // The professional step does not exist in link mode: renumber the rest.
  const shown = linked && step > 2 ? step - 1 : step;
  const total = linked ? TOTAL_STEPS - 1 : TOTAL_STEPS;
  return (
  <>
    <div className="rmob-header">
      <button type="button" className="rmob-back" onClick={onBack} aria-label="Volver">
        <RIcon name="chevL" width={20} height={20} />
      </button>
      <div className="rmob-header-center">
        <span className="rmob-header-title">{STEP_TITLES[step]}</span>
        <span className="rmob-header-sub">{salonName} · Paso {shown} de {total}</span>
      </div>
      <div className="rmob-header-spacer" />
    </div>
    <div className="rmob-progress">
      <div className="rmob-progress-bar" style={{ width: `${(shown / total) * 100}%` }} />
    </div>
  </>
  );
};

const Bar = ({ pill, label, amount, children, extra, onBack }) => (
  <div className="rmob-cta-bar">
    {extra}
    {pill && <div className="rmob-summary-pill">{pill}</div>}
    <div className="rmob-cta-actions">
      {onBack && (
        <button type="button" className="r-btn-soft rmob-back-btn" onClick={onBack}>
          <RIcon name="chevL" width={16} height={16} /> Volver
        </button>
      )}
      {label && (
        <div className="beauty-bar-total">
          <span className="beauty-cta-label">{label}</span>
          <span className="beauty-cta-amount">{amount}</span>
        </div>
      )}
      {children}
    </div>
  </div>
);

const Cta = ({ onClick, disabled, loading, children, type = "button", form }) => (
  <button
    type={type}
    form={form}
    className="r-btn-primary rmob-cta-btn"
    disabled={disabled || loading}
    onClick={onClick}
  >
    {loading ? <Spinner animation="border" size="sm" /> : <>{children} <RIcon name="arrowR" width={18} height={18} /></>}
  </button>
);

const Layout = ({ step, salonName, onBack, children, bar, linked }) => (
  <div className="reservas-root rmob-root rmob-step-fixed beauty-wizard">
    <StepHeader step={step} salonName={salonName} onBack={onBack} linked={linked} />
    {children}
    {bar}
  </div>
);

/**
 * Mobile booking wizard (6 steps) for a beauty salon. State and API calls live
 * in `useBeautyBookingFlow`; this component only renders and navigates.
 */
export default function BeautyBookingMobile({ salon, services, flow, onExit }) {
  const [step, setStep] = useState(flow.restoredStep === "resumen" ? 5 : flow.restoredStep === "slots" ? 4 : 1);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  // Service whose options sheet is open (add or edit).
  const [picking, setPicking] = useState(null);

  const {
    cartServices,
    baseTotals,
    effectiveTotals,
    holdTotals,
    availability,
    selectedSlot,
    hold,
    holdLoading,
    holdError,
    holdExpired,
    date,
    professionalId,
    selectedProfessional,
  } = flow;

  // The wizard is a fixed viewport: lock the page scroll behind it.
  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.scrollTo === "function" && process.env.NODE_ENV !== "test") {
      window.scrollTo(0, 0);
    }
  }, [step]);

  // A hold error belongs to the step that produced it: drop it when going back
  // before the slot step or leaving the wizard.
  const { clearHoldError } = flow;
  useEffect(() => {
    if (step < 4) clearHoldError();
  }, [step, clearHoldError]);
  useEffect(() => () => clearHoldError(), [clearHoldError]);

  const cartCount = cartServices.length;
  const hasDeposit = Number(holdTotals?.deposit) > 0;
  const professionalLabel = selectedProfessional ? selectedProfessional.name : "Sin preferencia";
  const durationLabel = formatDuration(effectiveTotals.durationMinutes);
  const slotStart = selectedSlot?.slotStart || hold?.slotStart || "";
  const slotEnd = hold?.slotEnd || flow.slotEndFor(selectedSlot);
  const timeRange = slotStart ? (slotEnd ? `${slotStart} a ${slotEnd}` : `${slotStart} hs`) : "";
  const shortDay = date ? dayjs(date).format("DD/MM") : "";

  const linked = Boolean(flow.linked);
  const goBack = () => {
    if (step === 1) return onExit?.();
    // Link mode has no professional step.
    if (linked && step === 3) return setStep(1);
    return setStep((s) => s - 1);
  };

  // ---- Step 1: Servicios ----
  const renderServices = () => {
    const categories = Array.from(new Set(services.map((s) => (s.category || "").trim()).filter(Boolean)));
    const needle = query.trim().toLowerCase();
    const visible = services.filter((s) => {
      if (category && (s.category || "").trim() !== category) return false;
      if (!needle) return true;
      return `${s.name} ${s.category || ""}`.toLowerCase().includes(needle);
    });
    const ids = cartServices.map((s) => String(s._id));
    const confirmPick = (choice) => {
      if (ids.includes(String(picking._id))) flow.updateService(picking, choice);
      else flow.addService(picking, choice);
      setPicking(null);
    };
    const pill = cartCount
      ? `${servicesLabel(cartCount)} · ${formatDuration(baseTotals.durationMinutes)} · ${baseTotals.deposit > 0 ? `Seña ${formatMoney(baseTotals.deposit)}` : "Sin seña"}`
      : "Todavía no agregaste servicios";

    return (
      <Layout step={step} salonName={salon.name} onBack={goBack} linked={linked}
        bar={
          <Bar pill={pill} label="Total" amount={formatMoney(baseTotals.price)} onBack={goBack}>
            <Cta disabled={!cartCount || (linked && Boolean(flow.maxDurationNotice))} onClick={() => setStep(linked ? 3 : 2)}>
              {cartCount ? "Continuar" : "Elegí al menos un servicio"}
            </Cta>
          </Bar>
        }
      >
        <div className="rmob-step-body">
          <LinkedProfessionalCard flow={flow} />
          <div className="beauty-search">
            <RIcon name="search" width={16} height={16} />
            <input
              type="search"
              placeholder="Buscar servicio"
              aria-label="Buscar servicio"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          {categories.length > 1 && (
            <div className="beauty-chip-row" role="group" aria-label="Categorías">
              {["", ...categories].map((c) => (
                <button
                  key={c || "all"}
                  type="button"
                  className={`beauty-filter-chip${category === c ? " is-active" : ""}`}
                  aria-pressed={category === c}
                  onClick={() => setCategory(c)}
                >
                  {c || "Todos"}
                </button>
              ))}
            </div>
          )}
          <p className="beauty-info-note">
            <RIcon name="info" width={15} height={15} />
            Podés sumar varios servicios en el mismo turno. Los horarios se calculan con la duración total.
          </p>

          {visible.length === 0 ? (
            <div className="rmob-empty">
              <p>No encontramos servicios con esa búsqueda.</p>
              <button type="button" className="r-btn-soft" onClick={() => { setQuery(""); setCategory(""); }}>
                Ver todos los servicios
              </button>
            </div>
          ) : (
            <ul className="beauty-pick-list">
              {visible.map((service) => {
                const added = ids.includes(String(service._id));
                const conOpciones = tieneOpciones(service);
                const elegido = added && conOpciones ? lineasDeCarrito([service], flow.cartChoices) : null;
                return (
                  <li key={service._id} className={`beauty-pick${added ? " is-added" : ""}`}>
                    <div className="beauty-pick-info">
                      <div className="beauty-service-name">{service.name}</div>
                      {service.description && <div className="beauty-service-desc">{service.description}</div>}
                      <div className="beauty-service-meta">
                        {elegido
                          ? `${formatDuration(elegido.reduce((t, l) => t + l.durationMinutes, 0))} · ${formatMoney(elegido.reduce((t, l) => t + l.price, 0))}`
                          : serviceMeta(service)}
                      </div>
                      {elegido && (elegido.length > 1 || elegido[0].variantName) && <div className="beauty-service-desc">{resumenDeLineas(elegido)}</div>}
                      {conOpciones && !added && <span className="beauty-soft-pill">{opcionesPill(service)}</span>}
                      <DepositBadge deposit={service.deposit} />
                    </div>
                    <div className="beauty-pick-side">
                      {added ? (
                        <>
                          <span className="beauty-added"><RIcon name="check" width={14} height={14} /> Agregado</span>
                          {conOpciones && (
                            <button
                              type="button"
                              className="r-btn-soft beauty-add-btn"
                              aria-label={`Editar opciones de ${service.name}`}
                              onClick={() => setPicking(service)}
                            >
                              Editar
                            </button>
                          )}
                          <button
                            type="button"
                            className="beauty-trash-btn"
                            aria-label={`Quitar ${service.name}`}
                            onClick={() => flow.removeService(service)}
                          >
                            <TrashIcon />
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="r-btn-soft beauty-add-btn"
                          aria-label={conOpciones ? `Elegir opciones de ${service.name}` : `Agregar ${service.name}`}
                          onClick={() => (conOpciones ? setPicking(service) : flow.addService(service))}
                        >
                          {conOpciones ? "Elegir" : "+ Agregar"}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <div style={{ height: 150 }} />
        </div>
        {picking && (
          <OpcionesSheet
            service={picking}
            initial={ids.includes(String(picking._id)) ? flow.cartChoices[String(picking._id)] : undefined}
            onConfirm={confirmPick}
            onClose={() => setPicking(null)}
          />
        )}
      </Layout>
    );
  };

  // ---- Step 2: Profesional ----
  const renderProfessional = () => {
    const options = flow.professionalOptions;
    const noneEligible = options.length > 0 && options.every((o) => !o.eligible);
    const pill = `${cartServices.map((s) => s.name).join(" + ")} · ${formatDuration(baseTotals.durationMinutes)}`;
    return (
      <Layout step={step} salonName={salon.name} onBack={goBack} linked={linked}
        bar={
          <Bar
            onBack={goBack}
            pill={pill}
            label={professionalId ? "Total" : "Desde"}
            amount={formatMoney(baseTotals.price)}
          >
            <Cta onClick={() => setStep(3)}>Continuar</Cta>
          </Bar>
        }
      >
        <div className="rmob-step-body">
          <span className="beauty-soft-pill">Opcional</span>
          <p className="beauty-step-text">
            Elegí un profesional o dejá que te asignemos el primero disponible.
          </p>
          <ProfessionalOptions flow={flow} />
          {noneEligible && (
            <div className="rmob-empty">
              <p>Ningún profesional hace todos los servicios de tu turno. Podés dejar Sin preferencia o cambiar tu selección.</p>
              <button type="button" className="r-btn-soft" onClick={() => setStep(1)}>Modificar servicios</button>
            </div>
          )}
          <ProfessionalNote />
          <div style={{ height: 150 }} />
        </div>
      </Layout>
    );
  };

  // ---- Step 3: Fecha ----
  const renderDate = () => {
    const windowDays = Number(salon.reservationWindowDays);
    const pill = `${cartServices.map((s) => s.name).join(" + ")} · ${durationLabel}${date ? ` · ${formatPillDate(date)}` : ""}`;

    return (
      <Layout step={step} salonName={salon.name} onBack={goBack} linked={linked}
        bar={
          <Bar pill={pill} label={effectiveTotals.from ? "Desde" : "Total"} amount={formatMoney(effectiveTotals.price)} onBack={goBack}>
            <Cta disabled={!date} onClick={() => setStep(4)}>Continuar</Cta>
          </Bar>
        }
      >
        <div className="rmob-step-body">
          <DayPicker flow={flow} salon={salon} />

          {date && (
            <div className="rmob-selected-date">
              <RIcon name="cal" width={16} height={16} />
              {formatLongDate(date)}
            </div>
          )}
          {Number.isFinite(windowDays) && windowDays > 0 && (
            <p className="beauty-footnote">Los turnos se pueden reservar hasta con {windowDays} días de antelación.</p>
          )}
          <div style={{ height: 150 }} />
        </div>
      </Layout>
    );
  };

  // ---- Step 4: Horario ----
  const renderSlots = () => {
    const isAny = availability.data?.mode === "any";
    const slotPrice = selectedSlot
      ? (isAny ? Number(selectedSlot.priceFrom) : Number(availability.data?.totalPrice))
      : effectiveTotals.price;
    const pill = selectedSlot
      ? `${shortDay} · ${timeRange} · ${professionalLabel}`
      : `${cartServices.map((s) => s.name).join(" + ")} · ${durationLabel}`;

    const onContinue = async () => {
      const result = await flow.createHold();
      if (result === "created") setStep(5);
    };

    const body = (
      <SlotPicker flow={flow} onChangeDate={() => setStep(3)} onChangeProfessional={linked ? flow.linked.leave : () => setStep(2)} />
    );

    return (
      <Layout step={step} salonName={salon.name} onBack={goBack} linked={linked}
        bar={
          <Bar
            onBack={goBack}
            pill={pill}
            label={selectedSlot ? (isAny ? "Desde" : "Total") : effectiveTotals.from ? "Desde" : "Total"}
            amount={formatMoney(slotPrice)}
            extra={
              <div className="beauty-hold-note">
                <RIcon name="clock" width={14} height={14} /> Al continuar te guardamos el turno por 5 minutos.
              </div>
            }
          >
            <Cta disabled={!selectedSlot} loading={holdLoading} onClick={onContinue}>Continuar</Cta>
          </Bar>
        }
      >
        <div className="rmob-step-body">
          <div className="beauty-context-card">
            <div>
              <strong>{date ? formatLongDate(date) : ""}</strong>
              <span>{durationLabel} · {professionalLabel}</span>
            </div>
            <button type="button" className="r-btn-soft" onClick={() => setStep(3)}>Cambiar</button>
          </div>
          <p className="beauty-info-note">
            <RIcon name="info" width={15} height={15} />
            Te mostramos solo los horarios en los que entran tus servicios ({durationLabel}).
          </p>
          {holdError && <Alert variant="warning">{holdError}</Alert>}
          {body}
          <div style={{ height: 190 }} />
        </div>
      </Layout>
    );
  };

  // Shared "hold lost" state for steps 5 and 6.
  const renderExpired = () => (
    <Layout step={step} salonName={salon.name} onBack={goBack} linked={linked}
      bar={
        <Bar>
          <button type="button" className="r-btn-soft rmob-back-btn" onClick={goBack}>
            <RIcon name="chevL" width={16} height={16} /> Volver
          </button>
        </Bar>
      }
    >
      <div className="rmob-step-body">
        <div className="beauty-expired">
          <RIcon name="clock" width={18} height={18} />
          <span>{holdExpired ? "El bloqueo expiró. Elegí un horario nuevamente." : "Todavía no elegiste un horario."}</span>
          <button
            type="button"
            className="r-btn-primary"
            onClick={() => { flow.dismissExpired(); setStep(4); }}
          >
            Elegir
          </button>
        </div>
        {holdError && <Alert variant="warning">{holdError}</Alert>}
      </div>
    </Layout>
  );

  const countdown = (
    <div className="rmob-countdown-bar">
      <HoldCountdown
        expiresAt={hold?.expiresAt}
        onExpired={flow.handleHoldExpired}
        label="Tu turno está reservado por"
      />
    </div>
  );

  // ---- Step 5: Resumen ----
  const renderSummary = () => {
    if (!hold || !holdTotals) return renderExpired();
    const policy = salonPolicyLines(salon).filter((line) => !/antelación/.test(line));
    const img = resolveUrl(salon.image || salon.mobileimage) || BEAUTY_FALLBACK_IMAGE;
    const address = salonAddressLine(salon);
    const barPill = `${formatPillDate(date)} · ${timeRange} · ${hold.professional?.name || professionalLabel}`;

    return (
      <Layout step={step} salonName={salon.name} onBack={goBack} linked={linked}
        bar={
          <Bar
            onBack={goBack}
            pill={barPill}
            label={hasDeposit ? "Seña" : "Total"}
            amount={formatMoney(hasDeposit ? holdTotals.deposit : holdTotals.total)}
          >
            <Cta onClick={() => setStep(6)}>Continuar</Cta>
          </Bar>
        }
      >
        {countdown}
        <div className="rmob-step-body">
          <div className="rmob-reserva-summary beauty-sum-flush">
            <div className="sum-img">
              <img src={img} alt={salon.name} onError={(e) => { e.currentTarget.src = BEAUTY_FALLBACK_IMAGE; }} />
              <div className="sc" />
              <div className="bn">{salon.name}</div>
            </div>
            <div className="beauty-sum-inner">
              {address && (
                <div className="rmob-rs-row"><RIcon name="pin" width={15} height={15} /><span>{address}</span></div>
              )}
              <HoldDetailRows hold={hold} holdTotals={holdTotals} date={date} professionalLabel={professionalLabel} professionalId={professionalId} />
              <h4 className="beauty-sum-title">Servicios</h4>
              {hold.services.map((s, i) => (
                <div className="rmob-rs-row" key={claveDeLinea(s, i)}>
                  <span>{nombreDeLinea(s)} <small>{formatDuration(s.durationMinutes)}</small></span>
                  <strong className="beauty-rs-value">{formatMoney(s.price)}</strong>
                </div>
              ))}
              <HoldAmounts holdTotals={holdTotals} hasDeposit={hasDeposit} />
            </div>
          </div>

          <div className="rmob-reserva-summary">
            <h4 className="beauty-sum-title">Política del local</h4>
            {policy.map((line) => <p className="beauty-policy-line" key={line}>{line}</p>)}
            {hasDeposit && <p className="beauty-policy-line">Si cancelás fuera de plazo, la seña no se devuelve.</p>}
          </div>
          <button type="button" className="beauty-link-btn" onClick={() => setStep(1)}>Modificar servicios</button>
          <div style={{ height: 150 }} />
        </div>
      </Layout>
    );
  };

  // ---- Step 6: Datos ----
  const renderContact = () => {
    if (!hold || !holdTotals) return renderExpired();
    const barPill = hasDeposit
      ? `Seña ${formatMoney(holdTotals.deposit)} · Saldo en el local ${formatMoney(holdTotals.payAtVenue)}`
      : `Total ${formatMoney(holdTotals.total)} · Pagás en el local`;

    const onSubmit = async (values) => {
      const result = await flow.onSubmit(values);
      if (result === "slots") setStep(4);
    };

    return (
      <Layout step={step} salonName={salon.name} onBack={goBack} linked={linked}
        bar={
          <Bar pill={barPill}>
            <button type="button" className="r-btn-soft rmob-back-btn" onClick={goBack}>
              <RIcon name="chevL" width={16} height={16} /> Volver
            </button>
            <Cta type="submit" form="beauty-booking-form" disabled={!flow.consent} loading={flow.submitting}>
              {hasDeposit ? "Ir al pago" : "Confirmar turno"}
            </Cta>
          </Bar>
        }
      >
        {countdown}
        <div className="rmob-step-body rmob-form-body">
          <h2 className="beauty-form-title">Completá tus datos</h2>
          <p className="beauty-step-text">Usamos estos datos para confirmar tu reserva y enviarte el comprobante.</p>

          <form id="beauty-booking-form" onSubmit={flow.handleSubmit(onSubmit)} noValidate>
            <ContactFields flow={flow} />
          </form>

          <AppointmentDetails hold={hold} holdTotals={holdTotals} date={date} professionalLabel={professionalLabel} professionalId={professionalId} />

          {hasDeposit && <DepositNote />}

          <ConsentCheck flow={flow} salon={salon} />

          {flow.submitError && <Alert variant="danger" className="mt-3">{flow.submitError}</Alert>}
          <div style={{ height: 160 }} />
        </div>

        <PaymentModal flow={flow} holdTotals={holdTotals} />
      </Layout>
    );
  };

  const renderers = { 1: renderServices, 2: renderProfessional, 3: renderDate, 4: renderSlots, 5: renderSummary, 6: renderContact };
  return renderers[step]();
}
